import React, { useMemo, useState } from 'react';
import { Autocomplete, TextField, Chip } from '@mui/material';
import { LocalOffer as TagIcon } from '@mui/icons-material';
import { PACKAGE_TAG_PREFIX } from '../utils/tags';

export interface TagEditorProps {
  value: string[];
  onChange: (tags: string[]) => void;
  /** Known tags offered as suggestions; already-selected ones are filtered out. */
  suggestions?: string[];
  /** Prefix that marks a tag as a grouping/package tag (visually emphasised). */
  packagePrefix?: string;
  placeholder?: string;
  disabled?: boolean;
}

const TagEditor: React.FC<TagEditorProps> = ({
  value,
  onChange,
  suggestions = [],
  packagePrefix = PACKAGE_TAG_PREFIX,
  placeholder,
  disabled,
}) => {
  const [inputValue, setInputValue] = useState('');

  const options = useMemo(
    () => suggestions.filter((t) => !value.includes(t)),
    [suggestions, value],
  );

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInputValue('');
  };

  const defaultPlaceholder = value.length === 0
    ? `Add tags (e.g. ${packagePrefix}credential-exposure)`
    : 'Add tag...';

  return (
    <Autocomplete
      multiple
      freeSolo
      size="small"
      disabled={disabled}
      options={options}
      value={value}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      onChange={(_, newValue) => {
        onChange(newValue as string[]);
        setInputValue('');
      }}
      renderTags={(tags, getTagProps) =>
        tags.map((tag, index) => {
          const isPackageTag = tag.startsWith(packagePrefix);
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              {...tagProps}
              label={tag}
              size="small"
              color={isPackageTag ? 'primary' : 'default'}
              variant={isPackageTag ? 'filled' : 'outlined'}
              icon={isPackageTag ? <TagIcon fontSize="small" /> : undefined}
              onDelete={() => onChange(value.filter((t) => t !== tag))}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder ?? defaultPlaceholder}
          variant="standard"
          InputProps={{ ...params.InputProps, disableUnderline: true }}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ',') && inputValue.trim()) {
              e.preventDefault();
              addTag(inputValue);
            }
          }}
        />
      )}
    />
  );
};

export default TagEditor;
